<template>
  <v-container class="input-text-size">
    <v-row v-if="canImportLockbox">
      <v-col cols="12">
        <v-card class="elevation-3 ma-3 pa-3">
          <upload-form @upload-queued="onUploadQueued" />
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-3 ma-3 pa-3">
          <queue-table
            ref="queueTable"
            @view-records="onViewRecords"
          />
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="selectedImportData" ref="detailsSection">
      <v-col cols="12">
        <v-card class="elevation-3 ma-3 pa-3">
          <records-table
            :import-data="selectedImportData"
            @close="selectedImportData = null"
            @refresh="refreshImportData"
            @update-record-status="onUpdateRecordStatus"
          />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import UploadForm from '@/components/tax/MortgageCoProcessing/LockboxUploadForm.vue';
import QueueTable from '@/components/tax/MortgageCoProcessing/LockboxQueueTable.vue';
import RecordsTable from '@/components/tax/MortgageCoProcessing/LockboxRecordsTable.vue';

export default {
  name: 'MortgageCoLockbox',
  components: {
    UploadForm,
    QueueTable,
    RecordsTable,
  },
  data() {
    return {
      selectedImportData: null,
      selectedQueueItem: null,
    };
  },
  computed: {
    ...mapGetters('auth/token', ['isTaxAdminAccessible', 'isMortgageCoAdminAccessible']),
    canImportLockbox() {
      return this.isTaxAdminAccessible || this.isMortgageCoAdminAccessible;
    },
  },
  methods: {
    ...mapActions('lockboxImport', [
      'loadLockboxImport',
    ]),
    onUploadQueued() {
      // Refresh the queue table when a new upload is queued
      if (this.$refs.queueTable) {
        this.$refs.queueTable.loadQueue();
      }
    },
    async onViewRecords(queueItem) {
      try {
        this.selectedQueueItem = queueItem;
        // Load the import data with records and batches
        const result = await this.loadLockboxImport({
          criteria: {
            fileQueueId: queueItem._id,
          },
          fields: {
            _id: 1,
            fileQueueId: 1,
            fileName: 1,
            postingDate: 1,
            totalRecords: 1,
            validRecords: 1,
            overpaymentRecords: 1,
            underpaymentRecords: 1,
            notFoundRecords: 1,
            duplicateRecords: 1,
            totalOverpaymentAmount: 1,
            totalUnderpaymentAmount: 1,
            missingFeesRecords: 1,
            paymentApplied: 1,
            batches: {
              batchNumber: 1,
              totalRecords: 1,
              validRecords: 1,
              overpaymentRecords: 1,
              underpaymentRecords: 1,
              notFoundRecords: 1,
              duplicateRecords: 1,
              missingFeesRecords: 1,
              paymentApplied: 1,
            },
            records: {
              batchNumber: 1,
              taxYear: 1,
              taxId: 1,
              propertyOrParcelId: 1,
              taxPayment: 1,
              penaltyPayment: 1,
              feePayment: 1,
              overpaymentAmount: 1,
              totalPayment: 1,
              paidDate: 1,
              expectedAmount: 1,
              expectedAmountPrevMonth: 1,
              expectedTax: 1,
              expectedPenalty: 1,
              expectedPenaltyPrevMonth: 1,
              expectedFee: 1,
              calculatedPenalty: 1,
              amountDifference: 1,
              validationStatus: 1,
              errorDescription: 1,
              // Adjustment tracking
              isAdjusted: 1,
              adjustedBy: 1,
              adjustedAt: 1,
              originalTaxPayment: 1,
              originalPenaltyPayment: 1,
              originalFeePayment: 1,
              originalOverpaymentAmount: 1,
              // Rematch tracking
              originalTaxYear: 1,
              originalTaxId: 1,
              isRematched: 1,
              rematchedBy: 1,
              rematchedAt: 1,
              // Approval tracking
              isApproved: 1,
              approvedBy: 1,
              approvedAt: 1,
              // Over/under and prev month flags
              hasOverUnderAdjustment: 1,
              usedPrevMonthPenalty: 1,
              // Missing-fees flag (bank file shows $0 fee but system has expectedFee > 0)
              isMissingFees: 1,
            },
          },
        });

        if (result && result.length > 0) {
          const importData = result[0];
          // Add originalIndex to each record for identification
          if (importData.records) {
            importData.records = importData.records.map((record, index) => ({
              ...record,
              originalIndex: index,
            }));
          }
          this.selectedImportData = importData;

          // Scroll to details section after DOM updates
          this.$nextTick(() => {
            const element = this.$refs.detailsSection;
            if (element) {
              // Check if it's a Vue component or native element
              const scrollTarget = element.$el || element;
              scrollTarget.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading import records:', error);
      }
    },
    async refreshImportData() {
      // Re-load the import data after payment creation or status update
      if (this.selectedQueueItem) {
        await this.onViewRecords(this.selectedQueueItem);
      }
    },
    onUpdateRecordStatus({ recordIndex, newStatus }) {
      // Update the record status in the local data
      if (this.selectedImportData && this.selectedImportData.records) {
        const record = this.selectedImportData.records.find(
          (r) => r.originalIndex === recordIndex,
        );
        if (record) {
          record.validationStatus = newStatus;
          record.errorDescription = null;
        }
      }
    },
  },
};
</script>

<style scoped>
</style>
